/**
 * How much of a long list a preview shows.
 *
 * The first twenty rows and the last five (PLAN §9). Both ends, because the two
 * things that go wrong with a pasted or imported list show up at opposite ends
 * of it: a header row or a stray blank at the top, an unpaired tail or a
 * half-copied selection at the bottom. The middle of ten thousand rows is not
 * something anyone reads, and rendering it costs a second of scrolling.
 *
 * Shared by the two-blocks preview and the import preview so they cannot drift
 * into showing different amounts of the same list.
 */

export const PREVIEW_HEAD = 20;
export const PREVIEW_TAIL = 5;

export interface PreviewSlice<T> {
  readonly head: readonly T[];
  /** How many rows the two ends stand in for. Zero when the whole list is shown. */
  readonly hidden: number;
  readonly tail: readonly T[];
}

/**
 * Splits a list into the part a preview shows and the count it elides.
 *
 * Deliberately does not build the row that stands for the gap: each preview
 * shapes that differently, and this function has no business knowing what a
 * row is.
 */
export function previewSlice<T>(rows: readonly T[]): PreviewSlice<T> {
  if (rows.length <= PREVIEW_HEAD + PREVIEW_TAIL) {
    return { head: rows, hidden: 0, tail: [] };
  }

  return {
    head: rows.slice(0, PREVIEW_HEAD),
    hidden: rows.length - PREVIEW_HEAD - PREVIEW_TAIL,
    tail: rows.slice(rows.length - PREVIEW_TAIL),
  };
}
